/**
 * Add Command
 *
 * Add a credential to the encrypted vault.
 */

import * as fs from 'fs-extra';
import { parse as parseEnv } from 'dotenv';
import { CredentialService } from '../core/service';
import { getEnvPath } from '../config/paths';
import * as output from '../utils/output';
import { promptCredentialValue } from '../utils/prompts';

/**
 * Read value from stdin (for piped input)
 */
async function readStdin(): Promise<string> {
  return new Promise((resolve, reject) => {
    let data = '';
    process.stdin.setEncoding('utf8');
    process.stdin.on('data', (chunk) => { data += chunk; });
    process.stdin.on('end', () => { resolve(data.trim()); });
    process.stdin.on('error', reject);

    // Handle case where stdin is a TTY (not piped)
    if (process.stdin.isTTY) {
      reject(new Error('No input provided via stdin. Use --value with a value or omit to use interactive prompt.'));
    }
  });
}

interface AddOptions {
  value?: string;
  fromEnv?: boolean;
  quiet?: boolean;
}

/**
 * Reserved/dangerous environment variable names that should be blocked
 */
const RESERVED_ENV_VARS = new Set([
  'PATH', 'HOME', 'USER', 'SHELL', 'PWD', 'OLDPWD',
  'LD_PRELOAD', 'LD_LIBRARY_PATH', 'LD_AUDIT',
  'DYLD_INSERT_LIBRARIES', 'DYLD_LIBRARY_PATH',
  'IFS', 'CDPATH', 'ENV', 'BASH_ENV',
  'TERM', 'DISPLAY', 'XAUTHORITY',
  'http_proxy', 'https_proxy', 'HTTP_PROXY', 'HTTPS_PROXY',
  'NODE_OPTIONS', 'NODE_PATH',
]);

export async function addCommand(name: string, options: AddOptions): Promise<void> {
  const service = new CredentialService();

  // Check vault exists
  const vaults = await service.hasVault();
  if (!vaults.local && !vaults.global) {
    output.error('No vault found. Run `secretsage init` first.');
    process.exit(1);
  }

  await service.init();

  // Validate credential name
  if (RESERVED_ENV_VARS.has(name) || RESERVED_ENV_VARS.has(name.toUpperCase())) {
    output.error(`Cannot use reserved environment variable name: ${name}`);
    process.exit(1);
  }

  if (!/^[A-Z][A-Z0-9_]*$/.test(name)) {
    output.warning(`Credential name "${name}" doesn't follow ENV convention (UPPER_SNAKE_CASE).`);
  }

  let value: string;

  if (options.fromEnv) {
    // Import from existing .env
    const envPath = getEnvPath();
    if (!(await fs.pathExists(envPath))) {
      output.error('No .env file found in current directory.');
      process.exit(1);
    }

    const content = await fs.readFile(envPath, 'utf8');
    const env = parseEnv(content);

    if (!(name in env)) {
      output.error(`Credential "${name}" not found in .env file.`);
      process.exit(1);
    }

    value = env[name];
    if (!options.quiet) {
      output.info(`Importing ${name} from .env`);
    }
  } else if (options.value === '-') {
    // Read from stdin
    try {
      value = await readStdin();
      if (!value) {
        output.error('Empty input received from stdin.');
        process.exit(1);
      }
    } catch (error) {
      output.error((error as Error).message);
      process.exit(1);
    }
  } else if (options.value) {
    // Use provided value
    value = options.value;
  } else {
    // Prompt for value
    value = await promptCredentialValue(name);
  }

  // Add to vault
  const spin = options.quiet ? null : output.spinner(`Encrypting ${name}...`);
  spin?.start();

  try {
    await service.add(name, value);
    spin?.stop();

    if (!options.quiet) {
      const vaultPath = await service.getVaultPath();
      output.credentialSaved(name, vaultPath);

      output.info('Grant this credential to .env with:');
      console.log(`  ${output.colors.highlight(`secretsage grant ${name}`)}`);
      console.log('');
    }
  } catch (error) {
    spin?.fail(`Failed to add ${name}`);
    output.error((error as Error).message);
    process.exit(1);
  }
}
